import React, { useEffect, useMemo, useState } from 'react';
import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  IconButton,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Divider,
  InputAdornment,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Add,
  Search,
  CheckCircle,
  Flag,
  Assignment,
  TrendingUp,
  VerifiedUser,
  PlayArrow,
  CalendarToday,
  TaskAlt,
  PendingActions,
  Cancel,
  Close,
  HourglassEmpty,
  ErrorOutline,
  Star,
  Send,
  PersonOutline,
  AccessTime,
  Description,
  SubdirectoryArrowRight,
  ExpandMore,
  ExpandLess,
  Home,
  ChevronRight,
  FiberManualRecord,
  ListAlt,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAllTags,
  getCreatedTasks,
  getTaskCategories,
  getTasks,
  getTaskStatuses,
  updateTaskStatus,
} from '../../redux/features/taskManagementSlice';
import CreateTask from './CreateTask';
import { useSnackbar } from 'notistack';
import TaskBoard, { statusById } from './DragAndDrop';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import MyTasksTable from './TasksTable';
import TasksTableFromDrag from './TasksTableFromDrag';

// ─── Mock Data ───────────────────────────────────────────────────────────────

// ─── Helpers ─────────────────────────────────────────────────────────────────

const priorityConfig = {
  1: {
    color: '#DC2626',
    bg: 'rgba(220,38,38,0.08)',
    label: 'Critical',
    icon: <ErrorOutline sx={{ fontSize: 16 }} />,
  },
  2: { color: '#F59E0B', bg: 'rgba(245,158,11,0.08)', label: 'High', icon: <Flag sx={{ fontSize: 16 }} /> },
  3: { color: '#3B82F6', bg: 'rgba(59,130,246,0.08)', label: 'Medium', icon: <Star sx={{ fontSize: 16 }} /> },
  4: { color: '#10B981', bg: 'rgba(16,185,129,0.08)', label: 'Low', icon: <CheckCircle sx={{ fontSize: 16 }} /> },
};

const statusConfig = {
  0: {
    color: '#F59E0B',
    bg: 'rgba(245,158,11,0.08)',
    label: 'Pending',
    icon: <HourglassEmpty sx={{ fontSize: 16 }} />,
  },
  1: {
    color: '#3B82F6',
    bg: 'rgba(59,130,246,0.08)',
    label: 'In Progress',
    icon: <PlayArrow sx={{ fontSize: 16 }} />,
  },
  2: {
    color: '#10B981',
    bg: 'rgba(16,185,129,0.08)',
    label: 'Completed',
    icon: <TaskAlt sx={{ fontSize: 16 }} />,
  },
  3: {
    color: '#EF4444',
    bg: 'rgba(239,68,68,0.08)',
    label: 'Cancelled',
    icon: <Cancel sx={{ fontSize: 16 }} />,
  },
};

const avatarColors = ['#6366F1', '#EC4899', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6'];

// ─── Stat Card (same as ReportingHeadView) ───────────────────────────────────

const StatCard = ({ icon, title, value, gradient, subtitle }) => (
  <Card
    sx={{
      position: 'relative',
      borderRadius: 3,
      overflow: 'hidden',
      border: 'none',
      color: '#fff',
      background: gradient,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      transition: 'all 0.3s ease',
      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 16px 36px rgba(0,0,0,0.20)' },
    }}
  >
    {/* Decorative circles */}
    <Box
      sx={{
        position: 'absolute',
        top: -30,
        right: -30,
        width: 110,
        height: 110,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.12)',
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        bottom: -40,
        right: 24,
        width: 80,
        height: 80,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.08)',
      }}
    />
    <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600, mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#fff', lineHeight: 1.1 }}>
            {value ?? 0}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.5, display: 'block' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: 2.5,
            background: 'rgba(255,255,255,0.2)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ─── Main Component ──────────────────────────────────────────────────────────

const MyTasks = () => {
  const { taskdata, loading, statusLoading, statusData, allTags } = useSelector((state) => state.taskManagement);

  const [tasks, setTasks] = useState(taskdata);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedTask, setSelectedTask] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [subtasksExpanded, setSubtasksExpanded] = useState(false);
  const [newSubtask, setNewSubtask] = useState('');

  // Task list filters: status is always applied (defaults to "all", so
  // pending tasks are never hidden just because of when they were created -
  // see the backend fix this replaces). The date range is opt-in - hidden
  // and unset until the user explicitly turns it on - and only narrows
  // things down further when actually set.
  const [statusFilter, setStatusFilter] = useState('all'); // all | pending | inprogress | overdue | completed
  const [dateRangeEnabled, setDateRangeEnabled] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  // Cross-module tag filter - shared with PMS goals. '' means no tag filter.
  const [tagFilter, setTagFilter] = useState('');

  const filters = useMemo(
    () => ({
      status: statusFilter,
      from_date: dateRangeEnabled && fromDate ? dayjs(fromDate).format('YYYY-MM-DD') : undefined,
      to_date: dateRangeEnabled && toDate ? dayjs(toDate).format('YYYY-MM-DD') : undefined,
      tag_id: tagFilter || undefined,
    }),
    [statusFilter, dateRangeEnabled, fromDate, toDate, tagFilter]
  );
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    assigneeDept: '',
    assigneeId: '',
    priority: 'medium',
    startDate: '',
    dueDate: '',
  });

  // View mode: 'board' or 'table'
  const [viewMode, setViewMode] = useState('board');

  const { enqueueSnackbar } = useSnackbar();

  const currentUser = JSON.parse(localStorage.getItem('cred')).profile;

  const departmentId = currentUser.departmentId;

  const employeesObj = taskdata?.[0] || {};

  const employeeNames = Object.keys(employeesObj);

  const firstEmployee = employeeNames[0];

  const mytasks = employeesObj[firstEmployee]?.tasks || [];

  // Stats
  const totalTasks = mytasks?.length;
  // Day-level comparison to match the backend's `due_date < CURDATE()` rule -
  // a task due later today shouldn't flip to overdue mid-day.
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const isOverdue = (t) => t.assignment_status !== 5 && new Date(t.expected_delivery_date) < startOfToday;
  // assignment_status_name has 5 different "Completed..." label variants
  // (see task_management.controller.js) depending on head-approval sub-status,
  // so match on the numeric assignment_status (5 = Completed) instead of the label.
  const completedTasks = mytasks.filter((t) => t.assignment_status === 5).length;
  // Overdue takes priority (matches the backend's pending/inprogress filters,
  // which are already defined as "not overdue") - a task that's In Progress or
  // Pending but past its due date is counted only as Overdue, not both.
  const inProgressTasks = mytasks.filter((t) => t.assignment_status_name === "In Progress" && !isOverdue(t)).length;
  const pendingTasks = mytasks.filter((t) => t.assignment_status_name === "Pending" && !isOverdue(t)).length;
  const overdueTasks = mytasks.filter(isOverdue).length;

  // Filter
  // const tasks = tasks.filter((t) => {
  //   const matchesSearch = t.title.toLowerCase().includes(searchQuery.toLowerCase());
  //   const matchesPriority = filterPriority === 'all' || t.priority === filterPriority;
  //   const matchesStatus = filterStatus === 'all' || t.status === filterStatus;
  //   return matchesSearch && matchesPriority && matchesStatus;
  // });

  // Handlers
  const handleOpenDetail = (task) => {
    setSelectedTask(task);
    setSubtasksExpanded(false);
    setNewSubtask('');
    setDetailDialogOpen(true);
  };

  const handleStatusChange = (task_assignment_id, newStatus) => {
    const payload = {
      task_assignment_id: task_assignment_id,
      assignment_status: newStatus,
    };

    // ✅ update UI instantly
    setSelectedTask((prev) => ({
      ...prev,
      current_status: newStatus,
    }));

    dispatch(updateTaskStatus(payload)).then((res) => {
      if (res.payload.status === 'success') {
        enqueueSnackbar(res.payload.message, { variant: 'success' });
        dispatch(getTasks(filters));
        setDetailDialogOpen(false);
      } else {
        enqueueSnackbar(res.payload.message, { variant: 'error' });
      }
    });
  };

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getTasks(filters));
    dispatch(getCreatedTasks(filters));
  }, [dispatch, filters]);

  useEffect(() => {
    dispatch(getTaskStatuses());
    dispatch(getTaskCategories(departmentId));
    dispatch(getAllTags());
  }, [dispatch, departmentId]);

  return (
    <>
      <GlobalWrapper>
        <Box sx={{ width: '100%', minHeight: '100vh' }}>
          {/* ── Header (matches ReportingHeadView) ─────────────────── */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
              borderRadius: 4,
              p: { xs: 3, sm: 4 },
              mb: 4,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -60,
                right: -60,
                width: 200,
                height: 200,
                borderRadius: '50%',
                background: 'rgba(99,102,241,0.12)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -40,
                left: '30%',
                width: 140,
                height: 140,
                borderRadius: '50%',
                background: 'rgba(236,72,153,0.08)',
              }}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
                position: 'relative',
                zIndex: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ width: 50, height: 50, bgcolor: '#6366F1', fontWeight: 700, fontSize: 18 }}>
                  {currentUser.avatar}
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#fff', mb: 0.5 }}>
                    My Tasks
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                    {currentUser.name} &middot; {currentUser.role}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setCreateDialogOpen(true)}
                sx={{
                  background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                  borderRadius: 2.5,
                  px: 3,
                  py: 1.2,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: 15,
                  boxShadow: '0 8px 24px rgba(99,102,241,0.3)',
                  '&:hover': { background: 'linear-gradient(135deg, #5558E6 0%, #7C4FE0 100%)' },
                }}
              >
                Create Task
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              width: '100%',
              mb: 2,
            }}
          >
            {/* Status filter - always visible. Everything shows by default. */}
            <FormControl size="small" sx={{ minWidth: 150, background: '#fff', borderRadius: 2 }}>
              <InputLabel id="task-status-filter-label">Show</InputLabel>
              <Select
                labelId="task-status-filter-label"
                label="Show"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All tasks</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="inprogress">In Progress</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>

            {/* Tag filter - cross-module, shared with PMS goals. */}
            <FormControl size="small" sx={{ minWidth: 150, background: '#fff', borderRadius: 2 }}>
              <InputLabel id="task-tag-filter-label">Tag</InputLabel>
              <Select
                labelId="task-tag-filter-label"
                label="Tag"
                value={tagFilter}
                onChange={(e) => setTagFilter(e.target.value)}
              >
                <MenuItem value="">All tags</MenuItem>
                {(allTags || [])
                  .filter((tag) => Number(tag?.appraisal_tag_status) === 1)
                  .map((tag) => (
                    <MenuItem key={tag.appraisal_tag_id} value={tag.appraisal_tag_id}>
                      {tag.appraisal_tag_name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {/* Date range - hidden and unset until the user opts in. */}
            {!dateRangeEnabled ? (
              <Button
                size="small"
                startIcon={<Add sx={{ fontSize: 16 }} />}
                onClick={() => setDateRangeEnabled(true)}
                sx={{ textTransform: 'none', fontWeight: 600, color: '#64748B' }}
              >
                Filter by created date
              </Button>
            ) : (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <DatePicker
                    label="From"
                    value={fromDate}
                    onChange={(newValue) => setFromDate(newValue)}
                    format="DD MMM YYYY"
                    slotProps={{ textField: { size: 'small', sx: { width: 160, background: '#fff', borderRadius: 2 } } }}
                  />
                  <DatePicker
                    label="To"
                    value={toDate}
                    onChange={(newValue) => setToDate(newValue)}
                    format="DD MMM YYYY"
                    minDate={fromDate || undefined}
                    slotProps={{ textField: { size: 'small', sx: { width: 160, background: '#fff', borderRadius: 2 } } }}
                  />
                  <Tooltip title="Clear date filter">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setDateRangeEnabled(false);
                        setFromDate(null);
                        setToDate(null);
                      }}
                    >
                      <Close sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </LocalizationProvider>
            )}
          </Box>

          {/* ── Stat Cards (same style as ReportingHeadView) ───────── */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={6} sm={6} md={2.4}>
              <StatCard
                icon={<Assignment />}
                title="Total Tasks"
                value={totalTasks}
                gradient="linear-gradient(135deg, #6366F1, #8B5CF6)"
                subtitle="Assigned to me"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2.4}>
              <StatCard
                icon={<PendingActions />}
                title="Pending"
                value={pendingTasks}
                gradient="linear-gradient(135deg, #F59E0B, #F97316)"
                subtitle="Not started"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2.4}>
              <StatCard
                icon={<TrendingUp />}
                title="In Progress"
                value={inProgressTasks}
                gradient="linear-gradient(135deg, #3B82F6, #2563EB)"
                subtitle="Working on"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2.4}>
              <StatCard
                icon={<TaskAlt />}
                title="Completed"
                value={completedTasks}
                gradient="linear-gradient(135deg, #10B981, #059669)"
                subtitle="Well done!"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2.4}>
              <StatCard
                icon={<ErrorOutline />}
                title="Overdue"
                value={overdueTasks}
                gradient="linear-gradient(135deg, #EF4444, #DC2626)"
                subtitle="Need attention"
              />
            </Grid>
          </Grid>

          {/* View toggle: Board or Table */}
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <Button
              variant={viewMode === 'board' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('board')}
              startIcon={<Assignment />}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Board View
            </Button>
            <Button
              variant={viewMode === 'table' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('table')}
              startIcon={<ListAlt />}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Table View
            </Button>
          </Box>

          {/* Render only selected view */}
          {viewMode === 'board' ? (
            <TaskBoard filters={filters} />
          ) : (
            <>
              {/* Table using taskdata (board-style header) */}
              <TasksTableFromDrag tasks={mytasks} filters={filters} />
            </>
          )}
          <MyTasksTable filters={filters} />
          {/* ── Create Task Dialog ─────────────────────────────────── */}
          {createDialogOpen && <CreateTask filters={filters} open={createDialogOpen} setOpen={setCreateDialogOpen} />}
        </Box>
      </GlobalWrapper>
    </>
  );
};

export default MyTasks;
